import _ from 'lodash'
import type { BeckyExperiments, BuilderComponentsHierarchy } from '@wix/thunderbolt-commons'
import { getBuilderComponentsData } from '@wix/thunderbolt-commons'
import type { FetchFn, ViewerClientSpecMap } from '@wix/thunderbolt-symbols'
import type { Logger, Structure, IManifestPublicElement } from '@wix/thunderbolt-becky-types'

export type BuilderTypeToReducedManifest = Record<string, IManifestPublicElement['reducedComponentModel']>

export const getCompIdToBuilderType = (structure: Structure): Record<string, string> => {
	const structureComponents = Object.values(structure)

	return structureComponents.reduce((acc, { id, builderType }) => {
		if (builderType) {
			acc[id] = builderType
		}
		return acc
	}, {} as Record<string, string>)
}

export const getCompIdToReducedManifest = (structure: Structure, builderTypeToReducedManifest: BuilderTypeToReducedManifest) => {
	const compIdToBuilderType: Record<string, string> = getCompIdToBuilderType(structure)

	return _.mapValues(compIdToBuilderType, (builderType) => builderTypeToReducedManifest[builderType])
}

const getUniqueBuilderTypesOnStructure = (structure: Structure): string[] => {
	const structureComponents = Object.values(structure)

	const builderTypes = structureComponents.reduce((acc, { builderType }) => {
		if (builderType) {
			acc.push(builderType)
		}
		return acc
	}, [] as string[])

	return Array.from(new Set(builderTypes))
}

const fetchManifest = async (builderType: string, viewerReducedModelUrl: string, fetchFn: FetchFn, logger: Logger) => {
	const manifest = await fetchFn(viewerReducedModelUrl)
		.then((response) => response.json())
		.then((result: IManifestPublicElement) => result.reducedComponentModel)
		.catch((error) => {
			logger.error(`Failed to fetch manifest for builderType: ${builderType} from ${viewerReducedModelUrl}`, error)
			return null
		})

	return manifest
}

export const getBuilderTypeToReducedManifest = async (structure: Structure,
	clientSpecMap: ViewerClientSpecMap,
	fetchFn: FetchFn,
	logger: Logger,
	isBuilderComponentModel: boolean,
	compTypeToAllDescendants: BuilderComponentsHierarchy,
	experiments: BeckyExperiments): Promise<BuilderTypeToReducedManifest> => {
	const manifestCache = new Map<string, IManifestPublicElement['reducedComponentModel']>()
	const structureBuilderTypes = getUniqueBuilderTypesOnStructure(structure)
	const builderComponentsData = getBuilderComponentsData(clientSpecMap, isBuilderComponentModel)

	// Loads a manifest for a given builder type (and its inner Ref Elements)
	const loadManifest = async (builderType: string) => {
		if (manifestCache.has(builderType)) {
			return
		}

		const viewerReducedModelUrl = builderComponentsData.components[builderType]?.viewerReducedModel

		if (!viewerReducedModelUrl) {
			logger.warn(`No Builder Component Data found for builderType: ${builderType}`)
			return
		}

		const manifest = await fetchManifest(builderType, viewerReducedModelUrl, fetchFn, logger)

		if (!manifest) {
			logger.warn(`No manifest found for builderType: ${builderType}`)
			return
		}

		logger.info(`Loaded manifest for builderType: ${builderType}`)

		// save the manifest to the cache
		manifestCache.set(builderType, manifest)

		// When the experiment is enabled, load the manifests for the inner Ref Elements
		if (experiments['specs.thunderbolt.supportExternalDependenciesCss']) {
			const innerRefElementsTypes = compTypeToAllDescendants[builderType] || []

			await Promise.all(innerRefElementsTypes.map((innerRefElementType) => loadManifest(innerRefElementType)))
		}

		return manifest
	}

	logger.info(`Loading manifests for builderTypes: ${structureBuilderTypes}`)

	// For each builder type, fetch the manifest recursively (in parallel)
	await Promise.all(
		structureBuilderTypes.map((builderType) => loadManifest(builderType))
	)

	logger.info(`Finished loading all manifests - builderTypes: ${Array.from(manifestCache.keys())}`)

	return Object.fromEntries(manifestCache.entries())
}
